import elefaMascot from "@/assets/elefa-app-icon.webp";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface ElefaLoadingScreenProps {
  className?: string;
  /** Hide the textual label below the animation */
  hideLabel?: boolean;
  /** Use full viewport height instead of 60dvh */
  fullScreen?: boolean;
}

/**
 * Modern, branded Elefa loading screen.
 * Concentric signal rings + breathing app icon evoke a live VoIP connection.
 */
export function ElefaLoadingScreen({
  className,
  hideLabel = false,
  fullScreen = false,
}: ElefaLoadingScreenProps) {
  const { t } = useTranslation("common");

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={t("loading", "Loading")}
      data-elefa-loading="true"
      className={cn(
        "flex flex-col items-center justify-center bg-background",
        fullScreen ? "min-h-[100dvh]" : "min-h-[60dvh]",
        className,
      )}
    >
      {/* Icon + signal rings */}
      <div className="relative flex items-center justify-center w-32 h-32">
        {/* Ambient glow */}
        <div
          aria-hidden
          className="absolute inset-0 rounded-full bg-gradient-to-br from-elefa-orange/30 via-elefa-gold/10 to-transparent blur-2xl"
        />

        {/* Concentric signal rings */}
        <span
          aria-hidden
          className="absolute inset-3 rounded-full border-2 border-elefa-orange/60 animate-elefa-ring"
          style={{ animationDelay: "0s" }}
        />
        <span
          aria-hidden
          className="absolute inset-3 rounded-full border-2 border-elefa-orange/50 animate-elefa-ring"
          style={{ animationDelay: "0.6s" }}
        />
        <span
          aria-hidden
          className="absolute inset-3 rounded-full border-2 border-elefa-orange/40 animate-elefa-ring"
          style={{ animationDelay: "1.2s" }}
        />

        {/* Branded icon - eager + high priority so it paints with the screen */}
        <img
          src={elefaMascot}
          alt=""
          aria-hidden
          draggable={false}
          loading="eager"
          decoding="async"
          // @ts-expect-error fetchpriority is a valid HTML attribute not yet in React types
          fetchpriority="high"
          className="relative w-20 h-20 object-contain rounded-[22%] animate-elefa-breathe select-none"
        />

      </div>

      {/* Label + animated dots */}
      {!hideLabel && (
        <div className="mt-6 flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <span>{t("loading", "Loading")}</span>
          <span className="flex items-center gap-1" aria-hidden>
            <span
              className="w-1.5 h-1.5 rounded-full bg-elefa-orange animate-elefa-dot"
              style={{ animationDelay: "0s" }}
            />
            <span
              className="w-1.5 h-1.5 rounded-full bg-elefa-orange animate-elefa-dot"
              style={{ animationDelay: "0.2s" }}
            />
            <span
              className="w-1.5 h-1.5 rounded-full bg-elefa-orange animate-elefa-dot"
              style={{ animationDelay: "0.4s" }}
            />
          </span>
        </div>
      )}

      <span className="sr-only">{t("loading", "Loading")}</span>
    </div>
  );
}
